/**
 * HousingCasePipelinePanel — the housing pipeline as seen from the case file.
 *
 * Extracted from Case.tsx, which had grown to 1168 lines with this bolted into
 * the bottom of it. The stage list is now the shared StageRail (same component
 * the citizen tracker uses, so both sides describe the journey identically) and
 * the committee award form is the shared decision component rather than an inline
 * copy with a hardcoded default amount.
 */
import { Link } from 'react-router-dom';
import type { HousingCaseView } from '@servicesai/api-client';
import {
  GlassButton,
  GlassCard,
  PriorityBandPill,
  StageRail,
  formatAed,
  useI18n,
  type Localized,
  type StageRailItem,
} from '@servicesai/ui';
import { admin } from '../../lib/api.js';
import {
  HousingCommitteeDecision,
  type CommitteeDecisionInput,
} from './HousingCommitteeDecision.js';

/**
 * The pipeline projection the case file receives. `HousingCaseView` in the client
 * is the Manzali award shape, so the pipeline shape is spelled out here.
 */
export interface HousingCasePipelineView {
  currentStageId: string;
  stages: Array<{
    id: string;
    label: Localized;
    status: 'pending' | 'in_progress' | 'completed';
    completedAt: string | null;
    notes: Localized | null;
  }>;
  research?: { readyForCommittee?: boolean } | null;
  committee: {
    outcome: 'approved' | 'rejected';
    assistanceType: 'grant' | 'loan' | null;
    amountAed: number | null;
    reasons: Localized[];
    decidedAt: string;
  } | null;
  priority?: {
    score: number;
    band: 'low' | 'medium' | 'high' | 'urgent';
    factors: Array<{ id: string; label: Localized; points: number }>;
    summary: Localized;
  } | null;
  suggestion?: {
    assistanceType: 'grant' | 'loan';
    amountAed: number;
    basis: Localized;
  } | null;
  reapplication?: { priorReference: string; priorOutcome: string } | null;
  simulated: boolean;
  updatedAt: string;
}

const COPY = {
  title: { en: 'Housing decision pipeline', ar: 'مسار قرار الإسكان' },
  body: {
    en: 'Advance the case through Assessment, Research, Committee and Decision Issuance. Cases with higher household need are reviewed first.',
    ar: 'قدّم الحالة عبر التقييم ثم البحث ثم اللجنة ثم إصدار القرار. تُراجع الحالات ذات الحاجة الأسرية الأعلى أولاً.',
  },
  reapplication: { en: 'Re-application of', ar: 'إعادة تقديم لـ' },
  demo: { en: 'Run full demo pipeline', ar: 'تشغيل مسار العرض كاملاً' },
  demoDone: { en: 'Demo pipeline completed and decision issued.', ar: 'اكتمل مسار العرض وصدر القرار.' },
  completeAssessment: { en: 'Complete assessment', ar: 'إكمال التقييم' },
  assessmentDone: { en: 'Assessment completed.', ar: 'اكتمل التقييم.' },
  openResearch: { en: 'Open field research', ar: 'فتح البحث الميداني' },
  completeResearch: { en: 'Complete research', ar: 'إكمال البحث' },
  researchDone: { en: 'Research completed.', ar: 'اكتمل البحث.' },
  issue: { en: 'Issue decision', ar: 'إصدار القرار' },
  issued: { en: 'Decision issued.', ar: 'صدر القرار.' },
  approved: { en: 'Committee approved.', ar: 'وافقت اللجنة.' },
  rejected: { en: 'Committee rejected.', ar: 'رفضت اللجنة.' },
  openCommittee: { en: 'Open committee session', ar: 'فتح جلسة اللجنة' },
  stageOwners: {
    application_submission: { en: 'Applicant', ar: 'مقدم الطلب' },
    application_assessment: { en: 'Housing reviewer', ar: 'مراجع الإسكان' },
    review_research: { en: 'Field research team', ar: 'فريق البحث الميداني' },
    committee_review: { en: 'Housing committee', ar: 'لجنة الإسكان' },
    decision_issuance: { en: 'Ministry', ar: 'الوزارة' },
  } as Record<string, Localized>,
};

/** Fallback stages so the card still reads as a journey before init. */
const FALLBACK_STAGES: HousingCasePipelineView['stages'] = [
  { id: 'application_submission', label: { en: 'Application Submission', ar: 'تقديم الطلب' }, status: 'completed', completedAt: null, notes: null },
  { id: 'application_assessment', label: { en: 'Application Assessment', ar: 'تقييم الطلب' }, status: 'in_progress', completedAt: null, notes: null },
  { id: 'review_research', label: { en: 'Review Research', ar: 'البحث والمراجعة الميدانية' }, status: 'pending', completedAt: null, notes: null },
  { id: 'committee_review', label: { en: 'Committee Review', ar: 'مراجعة اللجنة' }, status: 'pending', completedAt: null, notes: null },
  { id: 'decision_issuance', label: { en: 'Decision Issuance', ar: 'إصدار القرار' }, status: 'pending', completedAt: null, notes: null },
];

export function HousingCasePipelinePanel({
  reference,
  applicationState,
  housingCase,
  act,
}: {
  reference: string;
  applicationState: string;
  housingCase: HousingCasePipelineView | null;
  act: (fn: () => Promise<unknown>, okMessage?: string) => Promise<void>;
}) {
  const { l } = useI18n();
  const open = applicationState === 'submitted' || applicationState === 'in_review';
  const stages = housingCase?.stages ?? FALLBACK_STAGES;
  const current = housingCase?.currentStageId ?? 'application_assessment';
  const committee = housingCase?.committee ?? null;

  const items: StageRailItem[] = stages.map((stage) => ({
    id: stage.id,
    label: stage.label,
    status:
      committee?.outcome === 'rejected' && stage.id === 'committee_review'
        ? 'failed'
        : stage.status,
    actor: COPY.stageOwners[stage.id] ?? null,
    at: stage.completedAt,
    notes: stage.notes,
    body:
      stage.id === 'committee_review' && committee ? (
        <CommitteeOutcome committee={committee} />
      ) : undefined,
  }));

  const decide = (input: CommitteeDecisionInput) =>
    void act(
      () =>
        admin().recordHousingCommitteeDecision(reference, {
          outcome: input.outcome,
          assistanceType: input.assistanceType,
          amountAed: input.amountAed,
          reasons: input.reasons,
        }),
      l(input.outcome === 'approved' ? COPY.approved : COPY.rejected),
    );

  return (
    <GlassCard className="mt-4 border border-camel-600/25 bg-camel-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-extrabold">{l(COPY.title)}</div>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ink-soft">{l(COPY.body)}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            {housingCase?.priority ? (
              <PriorityBandPill
                band={housingCase.priority.band}
                score={housingCase.priority.score}
              />
            ) : null}
            {housingCase?.reapplication ? (
              <span className="text-[11px] font-bold text-ink-soft">
                {l(COPY.reapplication)}{' '}
                <span className="font-mono" dir="ltr">
                  {housingCase.reapplication.priorReference}
                </span>
              </span>
            ) : null}
          </div>
        </div>
        {open ? (
          <GlassButton
            variant="ghost"
            onClick={() =>
              void act(() => admin().simulateHousingDecision(reference, 'approved'), l(COPY.demoDone))
            }
          >
            {l(COPY.demo)}
          </GlassButton>
        ) : null}
      </div>

      <div className="mt-4 rounded-xl border border-camel-600/20 bg-white p-4">
        <StageRail items={items} />
      </div>

      {open ? (
        <div className="mt-4">
          {current === 'application_assessment' ? (
            <GlassButton
              variant="primary"
              onClick={() =>
                void act(
                  () =>
                    admin().completeHousingPipelineStage(reference, 'application_assessment', {
                      en: 'Application assessed as complete.',
                      ar: 'تم تقييم اكتمال الطلب.',
                    }),
                  l(COPY.assessmentDone),
                )
              }
            >
              {l(COPY.completeAssessment)}
            </GlassButton>
          ) : null}

          {current === 'review_research' ? (
            <div className="flex flex-wrap gap-2">
              <Link
                to={`/field-research/cases/${reference}`}
                className="aegov-btn btn-secondary btn-xs inline-flex min-h-10 items-center justify-center"
              >
                {l(COPY.openResearch)}
              </Link>
              <GlassButton
                variant="primary"
                onClick={() =>
                  void act(
                    () =>
                      admin().completeHousingPipelineStage(
                        reference,
                        'review_research',
                        {
                          en: 'Call and household social research visit completed.',
                          ar: 'اكتمل الاتصال وزيارة البحث الاجتماعي.',
                        },
                        { callCompleted: true, visitCompleted: true },
                      ),
                    l(COPY.researchDone),
                  )
                }
              >
                {l(COPY.completeResearch)}
              </GlassButton>
            </div>
          ) : null}

          {current === 'committee_review' ? (
            <div className="rounded-xl border border-camel-600/20 bg-white p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-extrabold text-ink">
                  {l({ en: 'Record the committee decision', ar: 'سجّل قرار اللجنة' })}
                </span>
                <Link
                  to="/housing/committee"
                  className="text-[11px] font-black text-seablue-800 underline underline-offset-2"
                >
                  {l(COPY.openCommittee)}
                </Link>
              </div>
              <HousingCommitteeDecision
                suggestion={housingCase?.suggestion ?? null}
                priority={housingCase?.priority ?? null}
                onDecide={decide}
              />
            </div>
          ) : null}

          {current === 'decision_issuance' ? (
            <GlassButton
              variant="primary"
              onClick={() =>
                void act(() => admin().issueHousingPipelineDecision(reference), l(COPY.issued))
              }
            >
              {l(COPY.issue)}
            </GlassButton>
          ) : null}
        </div>
      ) : null}
    </GlassCard>
  );
}

function CommitteeOutcome({
  committee,
}: {
  committee: NonNullable<HousingCasePipelineView['committee']>;
}) {
  const { l, lang } = useI18n();
  const approved = committee.outcome === 'approved';
  return (
    <div
      className={`rounded-lg border px-3 py-2.5 ${
        approved ? 'border-aegreen-600/30 bg-aegreen-50' : 'border-aered-600/30 bg-aered-50'
      }`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <span
          className={`text-[10px] font-black uppercase tracking-wider ${
            approved ? 'text-approve' : 'text-reject'
          }`}
        >
          {approved
            ? l(committee.assistanceType === 'grant' ? { en: 'Grant', ar: 'منحة' } : { en: 'Loan', ar: 'قرض' })
            : l({ en: 'Rejected', ar: 'مرفوض' })}
        </span>
        {approved && committee.amountAed != null ? (
          <span className="font-mono text-xs font-black tabular-nums text-ink" dir="ltr">
            {formatAed(committee.amountAed, lang)}
          </span>
        ) : null}
      </div>
      {committee.reasons.length > 0 ? (
        <ul className="mt-1.5 space-y-0.5">
          {committee.reasons.map((reason, index) => (
            <li key={index} className="text-[11px] leading-5 text-ink-soft">
              {l(reason)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Kept so Case.tsx can keep its existing prop name for the housing case. */
export type { HousingCaseView };
